import React, { useEffect, useState } from 'react';
import { apiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

interface Referral {
  id: string;
  referrer_name: string;
  is_valid: boolean;
  days_remaining?: number;
}

interface InterestedParty {
  id: string;
  name: string;
  role?: string;
  organisation?: string;
}

interface PatientReferralHeaderInfoProps {
  patientId: string;
  // Called when the user clicks through from the hover list -- each screen
  // decides what "view all" means for it (Patient Details switches to the
  // Interested Parties tab; Consult can do the same once it has tabs).
  onViewInterestedParties?: () => void;
  // Tone the text for the gradient header banner both screens use.
  light?: boolean;
}

/**
 * Shared "Referring Doctor / Interested Parties / Referral Status" block for
 * the patient header banner -- used on both Patient Details and the Consult
 * screen so the two never drift out of sync with each other.
 */
const PatientReferralHeaderInfo: React.FC<PatientReferralHeaderInfoProps> = ({ patientId, onViewInterestedParties, light = true }) => {
  const { token } = useAuth();
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [interestedParties, setInterestedParties] = useState<InterestedParty[]>([]);
  const [showHoverList, setShowHoverList] = useState(false);

  useEffect(() => {
    if (!patientId || !token) return;

    apiCall(`/patients/referrals/?patient_id=${patientId}`, { method: 'GET' }, token)
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          setReferrals(data.results || data || []);
        }
      })
      .catch((err) => console.error('Error loading referrals:', err));

    apiCall(`/patients/interested-parties/?patient_id=${patientId}`, { method: 'GET' }, token)
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          setInterestedParties(data.results || data || []);
        }
      })
      .catch((err) => console.error('Error loading interested parties:', err));
  }, [patientId, token]);

  // Most recently referred-by-date entry represents the current active referral.
  const primaryReferral = referrals[0];
  const labelClass = `text-xs uppercase tracking-wide mb-1 font-bold ${light ? 'opacity-70' : 'text-base-content opacity-60'}`;
  const valueClass = light ? '' : 'text-base-content';

  return (
    <div className="flex items-start gap-6 text-sm">
      <div>
        <div className={labelClass}>Referring Doctor</div>
        {primaryReferral ? (
          <div className={`font-medium ${valueClass}`}>{primaryReferral.referrer_name}</div>
        ) : (
          <div className="opacity-70">Not provided</div>
        )}
      </div>

      <div className="relative">
        <div className={labelClass}>Interested Parties</div>
        {interestedParties.length > 0 ? (
          <button
            type="button"
            className={`font-medium underline decoration-dotted ${valueClass}`}
            onMouseEnter={() => setShowHoverList(true)}
            onMouseLeave={() => setShowHoverList(false)}
            onClick={onViewInterestedParties}
          >
            {interestedParties.length} {interestedParties.length === 1 ? 'party' : 'parties'}
          </button>
        ) : (
          <button
            type="button"
            className={`opacity-70 hover:opacity-100 ${valueClass}`}
            onClick={onViewInterestedParties}
          >
            None recorded
          </button>
        )}

        {showHoverList && interestedParties.length > 0 && (
          <div
            className="absolute z-50 top-full left-0 mt-1 w-64 bg-base-100 text-base-content border border-base-300 rounded-lg shadow-lg p-3"
            onMouseEnter={() => setShowHoverList(true)}
            onMouseLeave={() => setShowHoverList(false)}
          >
            <ul className="space-y-1.5">
              {interestedParties.map((party) => (
                <li key={party.id} className="text-sm">
                  <span className="font-medium">{party.name}</span>
                  {party.role && <span className="opacity-60"> - {party.role}</span>}
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="mt-2 text-xs text-primary hover:underline"
              onClick={onViewInterestedParties}
            >
              View all
            </button>
          </div>
        )}
      </div>

      {primaryReferral && (
        <div>
          <div className={labelClass}>Referral Status</div>
          <span className={`badge badge-sm ${primaryReferral.is_valid ? 'badge-success' : 'badge-error'}`}>
            {primaryReferral.is_valid ? 'Current' : 'Expired'}
          </span>
        </div>
      )}
    </div>
  );
};

export default PatientReferralHeaderInfo;
